import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Zap, Calendar, User, CheckCircle, LogOut, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckinRapido() {
  const [eventi, setEventi] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedEvento) {
      fetchPresenze();
    }
  }, [selectedEvento]);

  const fetchData = async () => {
    try {
      const [eventiRes, volontariRes] = await Promise.all([
        fetch(`${API_URL}/api/eventi`, { credentials: 'include' }),
        fetch(`${API_URL}/api/volontari?stato=attivo`, { credentials: 'include' })
      ]);

      if (eventiRes.ok) {
        const data = await eventiRes.json();
        // Filter to show only upcoming/in progress events
        const activeEvents = data.filter(e => ['programmato', 'in_corso', 'bozza'].includes(e.stato));
        setEventi(activeEvents);
      }

      if (volontariRes.ok) {
        const data = await volontariRes.json();
        setVolontari(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const fetchPresenze = async () => {
    try {
      const response = await fetch(`${API_URL}/api/presenze?evento_id=${selectedEvento}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPresenze(data);
      }
    } catch (error) {
      console.error('Error fetching presenze:', error);
    }
  };

  const handleCheckin = async (volontarioId) => {
    if (!selectedEvento) {
      toast.error('Seleziona prima un evento');
      return;
    }

    setProcessing(volontarioId);
    try {
      const response = await fetch(`${API_URL}/api/presenze/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          volontario_id: volontarioId,
          evento_id: selectedEvento
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(
          data.tipo === 'checkin' 
            ? `Check-in registrato alle ${data.ora}`
            : `Check-out registrato alle ${data.ora}`
        );
        fetchPresenze();
      } else {
        toast.error('Errore durante la registrazione');
      }
    } catch (error) {
      toast.error('Errore durante la registrazione');
    } finally {
      setProcessing(null);
    }
  };

  const getVolontarioStatus = (volontarioId) => {
    const presenza = presenze.find(p => p.volontario_id === volontarioId);
    if (!presenza) return 'absent';
    if (presenza.ora_checkout) return 'completed';
    return 'checked_in';
  };

  const selectedEventoData = eventi.find(e => e.evento_id === selectedEvento);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="checkin-rapido-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title flex items-center gap-2">
          <Zap className="w-8 h-8 text-amber-500" />
          Check-in Rapido
        </h1>
        <p className="page-subtitle">
          Registra velocemente le presenze dei volontari durante gli eventi
        </p>
      </div>

      {/* Event Selector */}
      <Card className="border-2 border-[#0055BB]">
        <CardContent className="p-6">
          <div className="space-y-4">
            <label className="text-lg font-medium text-slate-900">Seleziona Evento</label>
            <Select value={selectedEvento} onValueChange={setSelectedEvento}>
              <SelectTrigger className="h-14 text-lg" data-testid="select-evento">
                <Calendar className="w-5 h-5 mr-2" />
                <SelectValue placeholder="Scegli l'evento..." />
              </SelectTrigger>
              <SelectContent>
                {eventi.length === 0 ? (
                  <SelectItem value="none" disabled>Nessun evento attivo</SelectItem>
                ) : (
                  eventi.map(evento => (
                    <SelectItem key={evento.evento_id} value={evento.evento_id}>
                      {evento.titolo} - {new Date(evento.data).toLocaleDateString('it-IT')}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {selectedEventoData && (
              <div className="flex items-center gap-4 text-sm text-slate-500 pt-2">
                <span>{selectedEventoData.luogo || 'Luogo non specificato'}</span>
                {selectedEventoData.orario_inizio && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedEventoData.orario_inizio} - {selectedEventoData.orario_fine || '...'}
                  </span>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Volontari Grid */}
      {selectedEvento ? (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            Tocca per registrare Check-in / Check-out
          </h2>
          
          {volontari.length === 0 ? (
            <Card>
              <CardContent className="py-16">
                <div className="empty-state">
                  <User className="empty-state-icon" />
                  <p className="empty-state-title">Nessun volontario attivo</p>
                  <p className="empty-state-text">
                    Aggiungi dei volontari attivi per poter registrare le presenze
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {volontari.map(volontario => {
                const status = getVolontarioStatus(volontario.volontario_id);
                const presenza = presenze.find(p => p.volontario_id === volontario.volontario_id);
                const isProcessing = processing === volontario.volontario_id;

                return (
                  <Card 
                    key={volontario.volontario_id}
                    className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                      status === 'checked_in' 
                        ? 'border-green-500 bg-green-50' 
                        : status === 'completed'
                        ? 'border-slate-300 bg-slate-50'
                        : 'hover:border-[#0055BB]'
                    }`}
                    onClick={() => !isProcessing && handleCheckin(volontario.volontario_id)}
                    data-testid={`checkin-card-${volontario.volontario_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            status === 'checked_in' 
                              ? 'bg-green-500 text-white' 
                              : status === 'completed'
                              ? 'bg-slate-400 text-white'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {isProcessing ? (
                              <div className="spinner w-5 h-5 border-white border-t-transparent" />
                            ) : status === 'checked_in' ? (
                              <CheckCircle className="w-6 h-6" />
                            ) : status === 'completed' ? (
                              <LogOut className="w-6 h-6" />
                            ) : (
                              <User className="w-6 h-6" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">
                              {volontario.cognome} {volontario.nome}
                            </p>
                            {presenza && (
                              <p className="text-sm text-slate-500">
                                {presenza.ora_checkin && `In: ${presenza.ora_checkin}`}
                                {presenza.ora_checkout && ` - Out: ${presenza.ora_checkout}`}
                              </p>
                            )}
                          </div>
                        </div>
                        <Badge className={
                          status === 'checked_in' 
                            ? 'bg-green-100 text-green-800' 
                            : status === 'completed'
                            ? 'bg-slate-100 text-slate-800'
                            : 'bg-slate-100 text-slate-600'
                        }>
                          {status === 'checked_in' ? 'Presente' : status === 'completed' ? 'Completato' : 'Assente'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16">
            <div className="empty-state">
              <Calendar className="empty-state-icon" />
              <p className="empty-state-title">Seleziona un evento</p>
              <p className="empty-state-text">
                Scegli un evento dal menu sopra per iniziare a registrare le presenze
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      {selectedEvento && presenze.length > 0 && (
        <Card className="bg-slate-50">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm text-slate-600">
                    {presenze.filter(p => p.ora_checkin && !p.ora_checkout).length} presenti
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-400" />
                  <span className="text-sm text-slate-600">
                    {presenze.filter(p => p.ora_checkout).length} usciti
                  </span>
                </div>
              </div>
              <p className="timestamp">
                Registrazione in tempo reale
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <div className="alert-info">
        <p className="font-medium mb-1">Come funziona</p>
        <ul className="text-sm space-y-1">
          <li>• Seleziona l'evento in corso dal menu</li>
          <li>• Tocca la scheda del volontario per registrare il check-in</li>
          <li>• Tocca di nuovo per registrare il check-out</li>
          <li>• Le ore vengono calcolate automaticamente</li>
        </ul>
      </div>
    </div>
  );
}
